/**
 * The `PeopleProfileSidebar`'s query params, which make it linkable and
 * refresh-safe: `?profile_user=<userId>&profile_view=compact|expanded`.
 *
 * `profile_user` alone decides whether the sidebar is open; `profile_view`
 * only picks the layout and is meaningless without a user, so the two are
 * always written and cleared together. Read and write them through these
 * helpers rather than spelling the keys inline.
 */

export const PROFILE_USER_PARAM = 'profile_user';
export const PROFILE_VIEW_PARAM = 'profile_view';

export type ProfileSidebarView = 'compact' | 'expanded';

const EXPANDED_VIEW: ProfileSidebarView = 'expanded';
const COMPACT_VIEW: ProfileSidebarView = 'compact';

export type ProfileSidebarUrlState = {
  /** The user whose profile is open, or `null` when the sidebar is closed. */
  userId: string | null;
  /** Layout. Absent or unrecognized values fall back to `compact`. */
  view: ProfileSidebarView;
};

/** Read the state out of a search string or a parsed `URLSearchParams`. */
export const parseProfileSidebarParams = (
  search: string | URLSearchParams
): ProfileSidebarUrlState => {
  const params =
    typeof search === 'string' ? new URLSearchParams(search) : search;
  const userId = params.get(PROFILE_USER_PARAM);

  return {
    userId: userId === null || userId === '' ? null : userId,
    view:
      params.get(PROFILE_VIEW_PARAM) === EXPANDED_VIEW
        ? EXPANDED_VIEW
        : COMPACT_VIEW,
  };
};

/**
 * Write the state into `params` in place, leaving every other param alone. A
 * `null` `userId` strips both keys, so a closed sidebar leaves no stale view
 * for the next open to inherit.
 */
export const applyProfileSidebarParams = (
  params: URLSearchParams,
  { userId, view }: ProfileSidebarUrlState
): void => {
  if (userId === null) {
    params.delete(PROFILE_USER_PARAM);
    params.delete(PROFILE_VIEW_PARAM);
    return;
  }

  params.set(PROFILE_USER_PARAM, userId);
  params.set(PROFILE_VIEW_PARAM, view);
};
